import type { HeadlineResponse, SiteCategoryResponse } from '@on3/api';
import { Hide } from '@on3/ui-lib/components/Hide';
import { SubscriptionProvider } from '@on3/ui-lib/components/Subscription/Provider';
import {
  SiteProvider,
  type SiteServerProps,
} from '@on3/ui-lib/contexts/SiteProvider';
import { ArticleProvider, useAuth } from '@on3/ui-lib/index';
import { type PageType } from '@on3/ui-lib/types/common-props';
import { Billboard } from '@public/components/Ads/Billboard';
import { MobileBanner } from '@public/components/Ads/MobileBanner';
import { Adblock } from '@public/components/Analytics/Adblock';
import { Quantcast } from '@public/components/Analytics/Quantcast';
import { Raptive } from '@public/components/Analytics/Raptive';
import { Sailthru } from '@public/components/Analytics/Sailthru';
import { Spiny } from '@public/components/Analytics/Spiny';
import { NetworkFooter } from '@public/components/Footer/NetworkFooter';
import { WeareuscFooter } from '@public/components/Footer/WeareuscFooter';
import { Header } from '@public/components/Header';
import { ModalProvider } from '@public/components/Modals';
import { Schema } from '@public/components/Schema/Schema';
import clsx from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';

import styles from './PageWrapper.module.scss';

interface IPageWrapperProps {
  title?: string;
  description?: string;
  image?: OGImage;
  ogType?: string;
  canonical?: string | null;
  /** For pages that don't show ads, we need to not load the adhesion. */
  hideAds?: boolean;
  children: React.ReactNode;
  noIndex?: boolean;
  siteData: SiteServerProps;
  // TODO: make this required in future task
  pageType?: PageType | null;
  alternateBackground?: boolean;
  article?: HeadlineResponse | null;
  category?: SiteCategoryResponse | null;
  schema?: object;
}
interface OGImage {
  src?: string;
  height?: number;
  width?: number;
}

const noHeight = { height: '0' };

export const PageWrapper = ({
  title,
  children,
  description,
  image,
  ogType,
  hideAds,
  canonical,
  noIndex = false,
  siteData,
  alternateBackground,
  pageType,
  ...props
}: IPageWrapperProps) => {
  const { currentSite } = siteData || {};

  const router = useRouter();
  const isArticle = pageType === 'article';
  const isRivals = router.pathname.startsWith('/rivals');
  const isHighSchool = router.pathname.startsWith('/high-school');
  let siteName = currentSite.siteName || 'On3';

  const { user } = useAuth();
  const showAdhesion = !isArticle || (isArticle && !user?.has);
  const adhesionUnit = user?.has
    ? 'mobile_adhesion_top'
    : 'mobile_adhesion_top_guest';

  // Incase the site isn't set to rivals
  if (isRivals) {
    siteName = 'Rivals';
  }

  if (!image?.src && !isArticle) {
    if (isRivals || isHighSchool) {
      image = {
        src: 'https://on3static.com/cdn-cgi/image/height=627,width=1200,quality=95,fit=cover/uploads/dev/assets/cms/2025/07/15133903/rivals-afi.png',
        height: 627,
        width: 1200,
      };
    } else {
      image = {
        src: 'https://on3static.com/cdn-cgi/image/height=627,width=1200,quality=95,fit=cover/static/on3/on3-share.jpeg',
        height: 627,
        width: 1200,
      };
    }
  }

  // We only want this to run on 2/10/25
  const isHome = pageType === 'home';
  const now = new Date();
  const centralTimeOffset = -6;
  const nowCentralTime = new Date(
    now.setHours(now.getHours() + centralTimeOffset),
  );

  // Ensure it's February 10, 2025 in Central Time
  const isTargetDate =
    nowCentralTime.getFullYear() === 2025 &&
    nowCentralTime.getMonth() === 1 &&
    nowCentralTime.getDate() === 10;

  const isOn3 = currentSite?.key === 44;
  const showBillboard = isTargetDate && isOn3 && isHome;

  return (
    <SiteProvider
      currentSite={siteData?.currentSite}
      defaultYears={siteData?.defaultYears}
      menuData={siteData?.menuData}
      pageType={pageType || 'other'}
      rankingSports={siteData?.rankingSports}
      secondaryMenuData={siteData?.secondaryMenuData}
      siblingSite={siteData?.siblingSite}
      siteUrls={siteData?.siteUrls}
      sportsData={siteData?.sportsData}
      trendingMenuData={siteData?.trendingMenuData}
    >
      <SubscriptionProvider subscriptionData={siteData?.subscriptionData}>
        <ModalProvider>
          <ArticleProvider {...props}>
            {props.schema && <Schema schema={props.schema} />}
            <Head>
              {title && (
                <>
                  <title key="title">{title}</title>
                  <meta content={title} key="ogTitle" property="og:title" />
                </>
              )}
              {description && (
                <>
                  <meta content={description} name="description" />
                  <meta
                    content={description}
                    key={description}
                    property="og:description"
                  />
                </>
              )}
              {image && (
                <>
                  <meta
                    content={image?.src}
                    key={image?.src}
                    property="og:image"
                  />
                  <meta content={`${image?.width}`} property="og:image:width" />
                  <meta
                    content={`${image?.height}`}
                    property="og:image:height"
                  />
                  <meta content={image?.src} property="cXenseParse:image" />
                </>
              )}
              <meta content={siteName} property="og:site_name" />

              {ogType && <meta content={ogType} property="og:type" />}
              {!isArticle && !ogType && (
                <meta content="website" property="og:type" />
              )}
              {canonical && <link href={canonical} rel="canonical" />}
              {canonical && <meta content={canonical} property="og:url" />}
              {noIndex && <meta content="noindex" name="robots" />}
            </Head>

            <Quantcast key={`quantcast-${router.asPath}`} />
            {!hideAds && <Raptive key={`raptive-${router.asPath}`} />}
            <Spiny key={`spiny-${router.asPath}`} />
            <Sailthru key={`sailthru-${router.asPath}`} />
            <Adblock key={`adblock-${router.asPath}`} />

            {/* Provide an accessible way to skip navigation */}
            <a
              className={styles.skipToContent}
              href="#to_content"
              id="skipToContent"
            >
              Skip to main content
            </a>
            <div className={styles.body}>
              <Header />
              <div id="to_content" />
              {showBillboard && <Billboard id="leader_top" />}
              <section
                className={clsx(styles.central, {
                  [styles.alternateBackground]: alternateBackground,
                })}
              >
                {children}
              </section>
              {currentSite?.key === 79 && <WeareuscFooter />}
              <NetworkFooter />

              {!user?.st?.includes('premium') && !hideAds && (
                <>
                  {showAdhesion && (
                    <div className={clsx(styles.adhesion, 'adhesionAd')}>
                      <MobileBanner id={adhesionUnit} />
                    </div>
                  )}
                  <div id="image_top" style={noHeight} />
                  <div id="home_adhesion" style={noHeight} />
                  <div id="OOP_1" style={noHeight} />
                  <div id="desktop_adhesion" style={noHeight} />
                </>
              )}
            </div>
            <div className={styles.blocker} id="blocker" />
            {hideAds && (
              <Hide
                classes={['.adthrive-ad', '.adhesionAd', '[data-gg-moat]']}
              />
            )}
          </ArticleProvider>
        </ModalProvider>
      </SubscriptionProvider>
    </SiteProvider>
  );
};
